import { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import companyBasedRequest from "../../request/companyBasedRequest";
import PhoneInput from "../common/phoneInput/phoneInput";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import CATEGORY_OPTIONS from "../../constants/categoryOptions";
import logo from "../../images/LogoFullText.png";
import "./listShopPage.css";
import RequestCategoryModal from "./requestCategoryModal/requestCategoryModal";

const ListShopPage = () => {
	const { language } = useContext(CombinedContext);
	const { ref } = useParams();

	const [shopName, setShopName] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [category, setCategory] = useState("");
	const [subCategory, setSubCategory] = useState("");
	const [categoryOpen, setCategoryOpen] = useState(false);
	const [subCategoryOpen, setSubCategoryOpen] = useState(false);
	const [otherDescription, setOtherDescription] = useState("");
	const [loading, setLoading] = useState(false);
	const [banner, setBanner] = useState({ text: "", type: "info", exiting: false });
	const [manualRef, setManualRef] = useState(ref || "");
	const [categoryModalOpen, setCategoryModalOpen] = useState(false);

	useEffect(() => {
		if (!banner || !banner.text) return;
		const exitTimer = setTimeout(() => setBanner((prev) => ({ ...prev, exiting: true })), 3000);
		const removeTimer = setTimeout(() => setBanner({ text: "", type: "info", exiting: false }), 3250);
		return () => {
			clearTimeout(exitTimer);
			clearTimeout(removeTimer);
		};
	}, [banner]);

	const handleSubmit = async () => {
    if (!shopName || !firstName || !lastName || !email || !phone) {
        setBanner({
            type: "error",
            text: language === "es" ? "Por favor completa todos los campos." : "Please fill in all required fields.",
        });
        return;
    }

    setBanner(null);
    setLoading(true);

    try {
        const calls = [
            companyBasedRequest.requestShopInvite({
                shopName,
                firstName,
                lastName,
                email,
                phone,
                category,
                subCategory,
                referralCode: manualRef || null,
            }),
        ];

        if (otherDescription) {
            calls.push(companyBasedRequest.requestNewCategory({
                description: otherDescription,
            }));
        }

        const [response] = await Promise.all(calls);

        const msg =
            response?.message?.[language] ||
            (language === "es" ? "Por favor revisa tu correo electrónico para más instrucciones." : "Please check your email for further instructions.");

        setBanner({ type: "success", text: msg });
        setShopName("");
        setFirstName("");
        setLastName("");
        setEmail("");
        setPhone("");
        setCategory("");
        setSubCategory("");
        setOtherDescription("");
    } catch (err) {
        console.error("ListShop error:", err);
        const msg =
            err?.response?.data?.error?.[language] ||
            err?.response?.data?.message?.[language] ||
            err.message ||
            (language === "es" ? "No se pudo enviar la solicitud." : "Failed to send request.");
        setBanner({ type: "error", text: msg });
    } finally {
        setLoading(false);
    }
};

	const selectedCategory = CATEGORY_OPTIONS.find((c) => c.value === category);
	const availableSubCategories = selectedCategory?.subCategories || [];

	return (
		<div className="sf-ls-root">
			<div className="sf-ls-split">
				{/* ── LEFT PANEL ── */}
				<div className="sf-ls-left">
					<img src={logo} alt="Sin Fila" className="sf-ls-logo" />

					<div className="sf-ls-left-body">
						<h2 className="sf-ls-left-title">{translations[language].listShop.leftTitle}</h2>
						<p className="sf-ls-left-sub">{translations[language].listShop.leftSubtitle}</p>

						<div className="sf-ls-perks">
							<div className="sf-ls-perk">
								<div className="sf-ls-perk-icon">🏷️</div>
								<div className="sf-ls-perk-text">
									<strong>{translations[language].listShop.perkFreeTitle}</strong>
									<span>{translations[language].listShop.perkFreeDesc}</span>
								</div>
							</div>
							<div className="sf-ls-perk">
								<div className="sf-ls-perk-icon">⚡</div>
								<div className="sf-ls-perk-text">
									<strong>{translations[language].listShop.perk1Title}</strong>
									<span>{translations[language].listShop.perk1Desc}</span>
								</div>
							</div>
							<div className="sf-ls-perk">
								<div className="sf-ls-perk-icon">📅</div>
								<div className="sf-ls-perk-text">
									<strong>{translations[language].listShop.perk2Title}</strong>
									<span>{translations[language].listShop.perk2Desc}</span>
								</div>
							</div>
							<div className="sf-ls-perk">
								<div className="sf-ls-perk-icon">🎁</div>
								<div className="sf-ls-perk-text">
									<strong>{translations[language].listShop.perk3Title}</strong>
									<span>{translations[language].listShop.perk3Desc}</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* ── RIGHT PANEL — FORM ── */}
				<div className="sf-ls-right">
					<div className="sf-ls-form-head">
						<div className="sf-ls-form-title">{translations[language].listShop.title}</div>
						<div className="sf-ls-form-sub">{translations[language].listShop.subtitle}</div>
					</div>

					{/* Shop name */}
					<div className="sf-ls-field">
						<label className="sf-ls-label">{translations[language].listShop.shopName}</label>
						<input
							className="sf-ls-input"
							placeholder={translations[language].listShop.shopName}
							value={shopName}
							onChange={(e) => setShopName(e.target.value)}
						/>
					</div>

					{/* Name row */}
					<div className="sf-ls-row2">
						<div className="sf-ls-field">
							<label className="sf-ls-label">{translations[language].registerShop.step1.firstName}</label>
							<input
								className="sf-ls-input"
								placeholder={translations[language].registerShop.step1.firstName}
								value={firstName}
								onChange={(e) => setFirstName(e.target.value)}
							/>
						</div>
						<div className="sf-ls-field">
							<label className="sf-ls-label">{translations[language].registerShop.step1.lastName}</label>
							<input
								className="sf-ls-input"
								placeholder={translations[language].registerShop.step1.lastName}
								value={lastName}
								onChange={(e) => setLastName(e.target.value)}
							/>
						</div>
					</div>

					{/* Email */}
					<div className="sf-ls-field">
						<label className="sf-ls-label">{translations[language].listShop.email}</label>
						<input
							className="sf-ls-input"
							type="email"
							placeholder={translations[language].listShop.email}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</div>

					{/* Phone */}
					<div className="sf-ls-field">
						<label className="sf-ls-label">{translations[language].listShop.phone}</label>
						<PhoneInput businessPhone={phone} setBusinessPhone={setPhone} />
					</div>

					{/* Category row */}
					<div className="sf-ls-row2">
						<div className="sf-ls-field">
							<label className="sf-ls-label">{language === "es" ? "Categoría" : "Category"}</label>
							<div className="sf-ls-dropdown-wrap">
								<button
									type="button"
									className={`sf-ls-dropdown-btn${category ? " sf-ls-selected" : ""}`}
									onClick={() => {
										setCategoryOpen((p) => !p);
										setSubCategoryOpen(false);
									}}
								>
									<span>
										{category
											? language === "es"
												? selectedCategory.labelES
												: selectedCategory.labelEN
											: language === "es"
												? "Tipo de negocio"
												: "Business category"}
									</span>
									<span className={`sf-ls-arrow${categoryOpen ? " open" : ""}`}>▼</span>
								</button>
								{categoryOpen && (
									<ul className="sf-ls-dropdown-list">
										{CATEGORY_OPTIONS.map((c) => (
											<li
												key={c.value}
												className="sf-ls-dropdown-item"
												onClick={() => {
													setCategory(c.value);
													setSubCategory("");
													setCategoryOpen(false);
												}}
											>
												{language === "es" ? c.labelES : c.labelEN}
											</li>
										))}
									</ul>
								)}
							</div>
						</div>

						<div className="sf-ls-field">
							<label className="sf-ls-label">{language === "es" ? "Subcategoría" : "Subcategory"}</label>
							<div className="sf-ls-dropdown-wrap">
								<button
									type="button"
									className={`sf-ls-dropdown-btn${subCategory ? " sf-ls-selected" : ""}${!category ? " sf-ls-disabled" : ""}`}
									disabled={!category}
									onClick={() => setSubCategoryOpen((p) => !p)}
								>
									<span>
										{subCategory
											? language === "es"
												? availableSubCategories.find((s) => s.value === subCategory)?.labelES
												: availableSubCategories.find((s) => s.value === subCategory)?.labelEN
											: language === "es"
												? "Subcategoría"
												: "Subcategory"}
									</span>
									<span className={`sf-ls-arrow${subCategoryOpen ? " open" : ""}`}>▼</span>
								</button>
								{subCategoryOpen && (
									<ul className="sf-ls-dropdown-list">
										{availableSubCategories.map((sc) => (
											<li
												key={sc.value}
												className="sf-ls-dropdown-item"
												onClick={() => {
													setSubCategory(sc.value);
													setSubCategoryOpen(false);
												}}
											>
												{language === "es" ? sc.labelES : sc.labelEN}
											</li>
										))}
									</ul>
								)}
							</div>
						</div>
					</div>
					<div className="sf-ls-other-cat-text">
						<span className="sf-ls-request-cat-link" onClick={() => setCategoryModalOpen(true)}>
							{translations[language].listShop.requestCategory}
						</span>
					</div>

					<div className="sf-ls-field">
						<label className="sf-ls-label">{translations[language].listShop.referralCode}</label>
						<input
							className="sf-ls-input"
							placeholder={translations[language].listShop.referralCodePlaceholder}
							value={manualRef}
							onChange={(e) => setManualRef(e.target.value.toUpperCase())}
						/>
					</div>

					<button className="sf-ls-submit" onClick={handleSubmit} disabled={loading}>
						{loading
							? translations[language].listShop.sending || "Sending..."
							: translations[language].listShop.registerShop}
					</button>

					<p className="sf-ls-signin">
						{translations[language].listShop.alreadyHaveAccount} <Link to="/signin">{translations[language].listShop.signIn}</Link>
					</p>
				</div>
			</div>

			{banner && <BannerMessageFixed type={banner.type} text={banner.text} />}
			<RequestCategoryModal
    open={categoryModalOpen}
    onClose={() => setCategoryModalOpen(false)}
    onSuggestionSubmit={(suggestion) => {
        setOtherDescription(suggestion);
        setBanner({ type: "success", text: language === "es" ? "Categoría guardada. Completa el formulario y envía." : "Category saved. Complete the form and submit.", exiting: false });
    }}
/>
		</div>
	);
};

export default ListShopPage;
